import { useCallback, useEffect, useMemo, useState } from 'react'

import type { WizardFarm } from '../../types/farmWizard'
import { useLots } from '../../hooks/useLots'
import { recordCrewEvent } from './crewModeTelemetry'
import {
  buildCrewLocationOptions,
  isLotStream,
  nextCrewLocation,
  readRememberedCrewLocation,
  resolveCrewLocation,
  writeRememberedCrewLocation,
  type CrewLocationOption,
  type CrewLocationStream
} from './crewLocation'

export type UseCrewLocationResult = {
  options: CrewLocationOption[]
  location: CrewLocationOption | null
  choose: (option: CrewLocationOption) => void
  pickerOpen: boolean
  openPicker: () => void
  closePicker: () => void
  /** The next location in office order, for "Siguiente: … →". */
  next: CrewLocationOption | null
}

/**
 * Crew Mode Field Bundle v0 §3 items 1–4: one location per farm shared by the
 * four streams. Reads the device's cached farm and lots only (O-5).
 */
export const useCrewLocation = (args: {
  stream: CrewLocationStream
  farmId: string
  farms: WizardFarm[]
}): UseCrewLocationResult => {
  const { stream, farmId, farms } = args
  const farm = useMemo(() => farms.find((candidate) => String(candidate.id) === farmId), [farms, farmId])
  const { lots, loading } = useLots({ farmId, enabled: Boolean(farmId) && isLotStream(stream) })

  const options = useMemo(() => buildCrewLocationOptions({ stream, farm, lots }), [stream, farm, lots])
  const [location, setLocation] = useState<CrewLocationOption | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)

  useEffect(() => {
    if (!farmId || !farm) return
    if (isLotStream(stream) && loading) return
    setLocation((current) => {
      if (current && options.some((option) => option.key === current.key)) {
        return options.find((option) => option.key === current.key) ?? current
      }
      const resolved = resolveCrewLocation(stream, options, readRememberedCrewLocation(farmId))
      // No silent default (findings #52): with nothing remembered, ask.
      if (!resolved && options.length > 0) setPickerOpen(true)
      return resolved
    })
  }, [farmId, farm, stream, options, loading])

  const choose = useCallback(
    (option: CrewLocationOption) => {
      setLocation(option)
      setPickerOpen(false)
      writeRememberedCrewLocation(farmId, { productionUnitId: option.productionUnitId, lotId: option.lotId })
      recordCrewEvent('location.change', { stream, farmId })
    },
    [farmId, stream]
  )

  return {
    options,
    location,
    choose,
    pickerOpen,
    openPicker: () => setPickerOpen(true),
    closePicker: () => setPickerOpen(false),
    next: nextCrewLocation(options, location)
  }
}
